import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { IconChevronLeftSmall, IconChevronRightSmall } from "@web-monorepo/dds-icons";
import { DDSIconButton } from "../../Buttons/DDSIconButton/DDSIconButton";
import { useHorizontalScroll } from "@web-monorepo/hooks";
import { useCombinedRefs } from "../../../shared/hooks";
import classes from "./DDSTabs.module.css";
import clsx from "clsx";

/** DDSTabs - based off of [ShadCN](https://ui.shadcn.com/docs/components/tabs). See [storybook](https://components.internal.classdojo.com/?path=/story/components-ddstabs--default) for usage. */
const DDSTabs = forwardRef<ElementRef<typeof TabsPrimitive.Root>, ComponentPropsWithoutRef<typeof TabsPrimitive.Root>>(
  (props, ref) => {
    const { children, className, ...rest } = props;
    return (
      <TabsPrimitive.Root ref={ref} className={[classes.container, className].filter(Boolean).join(" ")} {...rest}>
        {children}
      </TabsPrimitive.Root>
    );
  },
);

const DDSTabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    /** When true, suppresses the default bottom border under the tab row. */
    hideBorder?: boolean;
  }
>((props, ref) => {
  const { className, children, hideBorder, ...rest } = props;
  const { scrollContainerRef, canScrollLeft, canScrollRight, scrollTo } = useHorizontalScroll({
    elementMultiplier: 2, // Show ~2 tabs at a time
    debounceTime: 16, // ~60fps
  });

  const combinedRef = useCombinedRefs(ref, scrollContainerRef);

  return (
    <div
      className={clsx(
        className,
        classes.tabsListWrapper,
        canScrollLeft && classes.canScrollLeft,
        canScrollRight && classes.canScrollRight,
        hideBorder && classes.hideBorder,
      )}
    >
      {canScrollLeft && (
        <span className={clsx(classes.scrollButtonSlot, classes.scrollButtonSlotLeft)}>
          <DDSIconButton
            className={classes.scrollButton}
            data-name="scroll-tabs-left"
            onClick={() => scrollTo("left")}
            aria-label="Scroll tabs left"
            size="xsmall"
            variant="secondary"
          >
            <IconChevronLeftSmall size="s" />
          </DDSIconButton>
        </span>
      )}
      <TabsPrimitive.List ref={combinedRef} className={classes.tabsList} {...rest}>
        {children}
      </TabsPrimitive.List>
      {canScrollRight && (
        <span className={clsx(classes.scrollButtonSlot, classes.scrollButtonSlotRight)}>
          <DDSIconButton
            className={classes.scrollButton}
            data-name="scroll-tabs-right"
            onClick={() => scrollTo("right")}
            aria-label="Scroll tabs right"
            size="xsmall"
            variant="secondary"
          >
            <IconChevronRightSmall size="s" />
          </DDSIconButton>
        </span>
      )}
    </div>
  );
});
DDSTabsList.displayName = TabsPrimitive.List.displayName;

const DDSTabTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    /** The value of the tab trigger */
    value: string;
    /**
     * The size of the tab label
     * @default "medium"
     */
    size?: "medium" | "large";
    /** When true, prevents the user from interacting with the tab */
    disabled?: boolean;
    /**
     * The name will get used for automated product events.
     * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
     */
    ["data-name"]: string;
    experiments?: string[];
  }
>(({ size = "medium", experiments, children, ...rest }, ref) => (
  // `children` renders twice: once visible, once in an aria-hidden ghost span
  // that reserves the active/hover width so the row doesn't shift on weight
  // change. Pass label-like children only (text, icons, simple <span>/<AT>/
  // <DDSLabelText> wrappers) — anything with refs, ids, mount effects, or
  // focusable descendants would be silently duplicated. Test helpers should
  // match tabs by accessible name (e.g. `getByRole("tab", { name })`),
  // which excludes the ghost; raw `textContent` will see both copies.
  <TabsPrimitive.Trigger
    ref={ref}
    className={clsx(classes.tabsTrigger, classes[size])}
    {...rest}
    data-experiments={experiments}
  >
    <span className={classes.tabsTriggerLabel}>{children}</span>
    <span className={classes.tabsTriggerGhost} aria-hidden="true">
      {children}
    </span>
  </TabsPrimitive.Trigger>
));
DDSTabTrigger.displayName = TabsPrimitive.Trigger.displayName;

const DDSTabContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
    /** The value of the tab content */
    value: string;
  }
>((props, ref) => <TabsPrimitive.Content ref={ref} className={classes.tabsContent} {...props} />);
DDSTabContent.displayName = TabsPrimitive.Content.displayName;

export { DDSTabs, DDSTabsList, DDSTabTrigger, DDSTabContent };
